/**
 * Authentication Initializer
 * Handles global authentication state management and logout callbacks
 */

import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { authManager } from './authManager'
import { membershipActions } from '@redux'
import { membershipServices } from '@services'

/**
 * Hook to initialize global authentication management
 * This should be called once at the app root level
 * Note: Navigation callback will be set by components that have access to useNavigate
 */
export const useAuthInitializer = () => {
    const dispatch = useDispatch()

    useEffect(() => {
        // Check token validity on mount and handle accordingly
        const tokens = membershipServices.getTokens()
        if (tokens.accessToken) {
            if (authManager.isTokenValid()) {
                // Token is valid, restore authentication state
                dispatch(membershipActions.getCurrentMemberAsync())
            } else if (tokens.hasRefreshToken) {
                // Access token expired but refresh token exists, try to refresh
                authManager.attemptTokenRefresh().then(refreshed => {
                    if (refreshed) {
                        // Refresh successful, restore authentication state
                        dispatch(membershipActions.getCurrentMemberAsync())
                    } else {
                        // Refresh failed, silent logout
                        authManager.logout(true)
                    }
                })
            } else {
                // No refresh token, use silent logout
                authManager.logout(true)
            }
        }

        // Register global logout callback to update Redux state
        const unregister = authManager.onLogout(() => {
            dispatch(membershipActions.logout())
        })

        return () => {
            unregister()
        }
    }, [dispatch])
}

export default useAuthInitializer
